import PageSEO from '../components/PageSEO';
import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Mail, Lock, User, Globe, MapPin, AlertCircle } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import COUNTRY_CITIES from '../data/countryCities';
import { trackSignup } from '../utils/analytics';

const inputBase = {
  width: '100%', height: 46, borderRadius: 10,
  background: '#FAF8F5', border: '1px solid #ECE7E1',
  paddingLeft: 38, paddingRight: 14, fontSize: 14,
  color: '#1A2B3C', outline: 'none',
};

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    name: '', surname: '', email: '', password: '', passwordConfirm: '',
    country: '', city: '', language: 'tr', terms_accepted: false, privacy_accepted: false,
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect');

  const formatApiErrorDetail = (detail) => {
    if (detail == null) return "Bir hata oluştu. Lütfen tekrar deneyin.";
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).filter(Boolean).join(' ');
    if (detail?.msg) return detail.msg;
    return String(detail);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'country') setFormData(prev => ({ ...prev, country: value, city: '' }));
    else setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setError('');
    if (formData.password !== formData.passwordConfirm) { setError('Şifreler eşleşmiyor'); return; }
    if (!formData.terms_accepted || !formData.privacy_accepted) { setError('Kullanım şartlarını ve gizlilik politikasını kabul etmelisiniz'); return; }
    setLoading(true);
    try {
      const { passwordConfirm, ...registerData } = formData;
      await register(registerData);
      trackSignup();
      navigate(redirectTo || '/onboarding');
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #15263A 0%, #1E3348 80px, #F8F6F3 80px, #F8F6F3 100%)' }}>
      <PageSEO title="Kayıt Ol" description="DoktorCebinde hesabı oluşturun ve sağlığınızı takip etmeye başlayın" />
      <Header />

      <section className="py-12 px-4" data-testid="register-form-section">
        <div style={{ maxWidth: 560, margin: '0 auto' }}>
          <div style={{ background: '#fff', border: '1px solid #ECE7E1', borderRadius: 14, padding: 40 }} data-testid="register-form-card">
            <div className="text-center mb-8">
              <h2 style={{ fontSize: 28, fontWeight: 700, color: '#1A2B3C', letterSpacing: '-0.02em' }}>
                Kayıt Ol
              </h2>
              <p style={{ fontSize: 15, color: '#687482', marginTop: 8 }}>
                Hesabınızı oluşturun ve başlayın
              </p>
            </div>

            {error && (
              <div className="mb-5 flex items-start gap-2 p-3" style={{ background: 'rgba(239,68,68,0.05)', border: '1px solid rgba(239,68,68,0.12)', borderRadius: 10 }} data-testid="register-error-message">
                <AlertCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                <p style={{ fontSize: 13, color: '#DC2626', margin: 0 }}>{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#435160', marginBottom: 6 }}>Ad</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2" style={{ width: 16, height: 16, color: '#94A3B8' }} />
                    <input name="name" type="text" value={formData.name} onChange={handleChange}
                      style={inputBase} placeholder="Adınız" required data-testid="register-name-input" />
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#435160', marginBottom: 6 }}>Soyad</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2" style={{ width: 16, height: 16, color: '#94A3B8' }} />
                    <input name="surname" type="text" value={formData.surname} onChange={handleChange}
                      style={inputBase} placeholder="Soyadınız" required data-testid="register-surname-input" />
                  </div>
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#435160', marginBottom: 6 }}>E-posta</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2" style={{ width: 16, height: 16, color: '#94A3B8' }} />
                  <input name="email" type="email" value={formData.email} onChange={handleChange}
                    style={inputBase} placeholder="ornek@email.com" required data-testid="register-email-input" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#435160', marginBottom: 6 }}>Şifre</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2" style={{ width: 16, height: 16, color: '#94A3B8' }} />
                    <input name="password" type="password" value={formData.password} onChange={handleChange}
                      style={inputBase} placeholder="••••••••" required data-testid="register-password-input" />
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#435160', marginBottom: 6 }}>Şifre Tekrar</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2" style={{ width: 16, height: 16, color: '#94A3B8' }} />
                    <input name="passwordConfirm" type="password" value={formData.passwordConfirm} onChange={handleChange}
                      style={inputBase} placeholder="••••••••" required data-testid="register-password-confirm-input" />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#435160', marginBottom: 6 }}>Ülke</label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none z-10" style={{ width: 16, height: 16, color: '#94A3B8' }} />
                    <select name="country" value={formData.country} onChange={handleChange} required
                      style={{ ...inputBase, appearance: 'none' }} data-testid="register-country-select">
                      <option value="">Ülke seçiniz</option>
                      <option value="Türkiye">Türkiye</option>
                      <option value="Almanya">Almanya</option>
                      <option value="Amerika Birlesik Devletleri">Amerika Birleşik Devletleri</option>
                      <option value="Avusturya">Avusturya</option>
                      <option value="Azerbaycan">Azerbaycan</option>
                      <option value="Belcika">Belçika</option>
                      <option value="Birlesik Krallik">Birleşik Krallık</option>
                      <option value="Bulgaristan">Bulgaristan</option>
                      <option value="Fransa">Fransa</option>
                      <option value="Hollanda">Hollanda</option>
                      <option value="Isvec">İsveç</option>
                      <option value="Isvicre">İsviçre</option>
                      <option value="Italya">İtalya</option>
                      <option value="Kanada">Kanada</option>
                      <option value="Kuzey Kibris">Kuzey Kıbrıs</option>
                      <option value="Norvec">Norveç</option>
                      <option value="Romanya">Romanya</option>
                      <option value="Rusya">Rusya</option>
                      <option value="Yunanistan">Yunanistan</option>
                      <option value="Diger">Diğer</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#435160', marginBottom: 6 }}>Şehir</label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none z-10" style={{ width: 16, height: 16, color: '#94A3B8' }} />
                    {formData.country && COUNTRY_CITIES[formData.country] ? (
                      <select name="city" value={formData.city} onChange={handleChange} required
                        style={{ ...inputBase, appearance: 'none' }} data-testid="register-city-select">
                        <option value="">Şehir seçiniz</option>
                        {COUNTRY_CITIES[formData.country].map(c => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    ) : (
                      <input name="city" type="text" value={formData.city} onChange={handleChange}
                        style={inputBase} placeholder={formData.country ? "Şehir yazınız" : "Önce ülke seçiniz"}
                        required disabled={!formData.country} data-testid="register-city-input" />
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-3 pt-2">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" name="terms_accepted" checked={formData.terms_accepted} onChange={handleChange}
                    className="mt-1 w-4 h-4 rounded" style={{ accentColor: '#1A2B3C' }} required data-testid="register-terms-checkbox" />
                  <span style={{ fontSize: 13, color: '#687482' }}>
                    <Link to="/terms" style={{ color: '#D88A61', fontWeight: 500, textDecoration: 'none' }} target="_blank">Kullanım Şartlarını</Link> okudum ve kabul ediyorum
                  </span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" name="privacy_accepted" checked={formData.privacy_accepted} onChange={handleChange}
                    className="mt-1 w-4 h-4 rounded" style={{ accentColor: '#1A2B3C' }} required data-testid="register-privacy-checkbox" />
                  <span style={{ fontSize: 13, color: '#687482' }}>
                    <Link to="/privacy" style={{ color: '#D88A61', fontWeight: 500, textDecoration: 'none' }} target="_blank">Gizlilik Politikasını</Link> okudum ve kabul ediyorum
                  </span>
                </label>
              </div>

              <button type="submit" disabled={loading} className="w-full disabled:opacity-50" style={{
                background: '#1A2B3C', color: '#fff', borderRadius: 4, padding: '14px',
                fontWeight: 600, fontSize: 15, border: 'none', cursor: 'pointer',
                transition: 'all 0.2s',
              }} data-testid="register-submit-button">
                {loading ? 'Kayıt Yapılıyor...' : 'Kayıt Ol'}
              </button>
            </form>

            <div className="mt-6 text-center">
              <p style={{ fontSize: 13, color: '#687482' }}>
                Zaten hesabın var mı?{' '}
                <Link to="/login" style={{ color: '#D88A61', fontWeight: 600, textDecoration: 'none' }} data-testid="login-link">Giriş Yap</Link>
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default RegisterPage;
